import React, { useEffect, useMemo, useState, useRef } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";
import { getClaims } from "../../../utils/auth";

function formatMiles(v) {
  if (v === "" || v == null) return "";
  const n = Number(String(v).replace(/\./g, ""));
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("es-PY");
}

function parseMiles(v) {
  if (v === "" || v == null) return "";
  const clean = String(v).replace(/\./g, "");
  const n = Number(clean);
  return Number.isFinite(n) ? String(n) : "";
}

export default function EditarTarjeta({
  tarjeta = {},
  onClose = () => {},
  onSaved = () => {},
}) {
  const ESTADO_CANCELADA_ID = "19";
  const [confirmCancel, setConfirmCancel] = useState(false);

  const doSave = async () => {
    setError("");

    if (Object.keys(errs).length > 0) return;

    const payload = {};

    const eId = form.estadoId ? Number(form.estadoId) : null;
    payload.estadoId = eId;

    const vLimite = numOrNull(form.valorLimite);
    const vLimiteOrig = numOrNull(view.valorLimite);

    const vCap = numOrNull(form.capacidadTanque);
    const vCapOrig = numOrNull(view.capacidadTanque);

    const vKm = numOrNull(form.ultimoKm);
    const vKmOrig = numOrNull(view.ultimoKm);

    const esCancelacion = String(form.estadoId) === ESTADO_CANCELADA_ID;

    // Para respetar legacy: cuando se cancela, se envían los campos numéricos actuales
    // aunque no hayan cambiado, porque el backend los compara contra la tarjeta guardada.
    if (esCancelacion) {
      payload.valorLimite = vLimite ?? 0;
      payload.capacidadTanque = vCap ?? 0;
      payload.ultimoKm = vKm ?? 0;
    } else {
      if (vLimite !== vLimiteOrig) payload.valorLimite = vLimite;
      if (vCap !== vCapOrig) payload.capacidadTanque = vCap;
      if (vKm !== vKmOrig) payload.ultimoKm = vKm;
    }

    if ((form.dataCentroCosto ?? "") !== (view.dataCentroCosto ?? "")) {
      payload.dataCentroCosto = form.dataCentroCosto ?? "";
    }

    if ((form.textoObservaciones ?? "") !== (view.textoObservaciones ?? "")) {
      payload.textoObservaciones = form.textoObservaciones ?? "";
    }

    if ((form.nroMovil ?? "") !== (view.nroMovil ?? "")) {
      payload.nroMovil = form.nroMovil ?? "";
    }

    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    try {
      setSaving(true);

      const updated = await appFetchJson(apiRoutes.tarjetas.update(view.id), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      onSaved(updated);
      onClose();
    } catch (e) {
      setError(e.message || "Error al guardar");
    } finally {
      setSaving(false);
      setConfirmCancel(false);
    }
  };

  // Cerrar con ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // ====== FETCH INIT (GET /tarjetas/{id}/editar) ======
  const tarjetaId = useMemo(
    () => tarjeta?.id ?? tarjeta?.tarjetaId ?? tarjeta?.numero,
    [tarjeta],
  );

  const [init, setInit] = useState(null);
  const [loadingInit, setLoadingInit] = useState(false);
  const [initError, setInitError] = useState("");

  const devRunRef = useRef(false);

  useEffect(() => {
    if (!tarjetaId) return;

    if (process.env.NODE_ENV !== "production") {
      if (!devRunRef.current) {
        devRunRef.current = true;
        return;
      }
    }

    const ac = new AbortController();
    setLoadingInit(true);
    setInitError("");

    (async () => {
      try {
        const json = await appFetchJson(
          apiRoutes.tarjetas.editInit(tarjetaId),
          { signal: ac.signal },
        );
        if (!ac.signal.aborted) setInit(json);
      } catch (e) {
        if (ac.signal.aborted) return;
        setInitError(e?.message || "Error al cargar datos de edición");
      } finally {
        if (!ac.signal.aborted) setLoadingInit(false);
      }
    })();

    return () => ac.abort();
  }, [tarjetaId]);

  // ====== Opciones del combo Estados ======
  const [estadosOpts, setEstadosOpts] = useState([]);

  useEffect(() => {
    let mounted = true;

    async function loadEstados() {
      try {
        const raw = await appFetchJson(apiRoutes.tarjetas.estados);

        const arr = Array.isArray(raw?.items)
          ? raw.items
          : Array.isArray(raw)
          ? raw
          : [];

        let opts = arr
          .map((e) => {
            const id =
              e?.ESTA_SQ_COD ??
              e?.esta_sq_cod ??
              e?.id ??
              e?.value ??
              "";
            const nombre =
              e?.ESTA_NM_ESTADO ??
              e?.esta_nm_estado ??
              e?.nombre ??
              e?.label ??
              "";

            return {
              value: String(id ?? ""),
              label: String(nombre ?? ""),
            };
          })
          .filter((o) => o.label);

        //const claims = getClaims();
        //const clienteId = claims?.clientId;

        //if (clienteId) {
        const permitidos = ["29", "21", "19"];
        opts = opts.filter((o) => permitidos.includes(o.value));
        //}

        if (mounted) {
          setEstadosOpts(opts);
        }
      } catch {
        if (mounted) {
          setEstadosOpts([]);
        }
      }
    }

    loadEstados();

    return () => {
      mounted = false;
    };
  }, []);

  // ===== Normalización de campos para la vista =====
  const view = useMemo(() => {
    const t = (init?.tarjeta ?? tarjeta) || {};

    const usuarioSolicitudVisible =
      t.usuarioSolicitudCodigo ??
      t.usuarioCodigo ??
      t.usuaCdUsuario ??
      t.usuarioSolicitud ??
      t.usuarioLogin ??
      t.usuario?.codigo ??
      t.usuario?.login ??
      null;

    return {
      id: t.id ?? t.tarjetaId ?? t.numero,
      numero: t.numero ?? t.nroTarjeta ?? t.tarjetaNumero,
      clienteCodigo: t.clienteCodigo ?? t.codCliente ?? t.codigoCliente,
      clienteNombre: t.clienteNombre ?? t.cliente ?? t.nombreCliente,
      eess: t.eessNombre ?? t.eessCodigo ?? t.codigoEESS,
      tipo: t.codigoTipo ?? t.tipo ?? t.tipoTarjeta ?? t.tipoCliente,

      estadoId: t.estadoId ?? null,
      estadoNombre: t.estadoNombre ?? t.estado,

      chapa: t.chapa ?? t.patente ?? t.matricula,
      marca: t.vehiculoMarca ?? t.marca,
      modelo: t.vehiculoModelo ?? t.modelo,
      fechaAlta: t.fechaAlta ?? t.dataFechaAlta,
      fechaBaja: t.fechaBaja ?? t.dataFechaBaja,
      fechaVigenciaInicio:
        t.fechaVigenciaInicio ?? t.dataFechaVigenciaIni ?? t.vigenciaInicio,
      fechaVigenciaFin:
        t.fechaVigenciaFin ?? t.dataFechaVigenciaFin ?? t.vigenciaFin,
      valorSaldo: t.valorSaldo,
      saldoDisponibleCliente: t.saldoDisponibleCliente,
      valorLimite: t.valorLimite,
      dataCentroCosto: t.dataCentroCosto ?? t.centroCosto,
      textoObservaciones:
        t.textoObservaciones ?? t.observaciones ?? t.obs ?? t.dataObs,
      capacidadTanque: t.capacidadTanque,
      ultimoKm:
        t.ultimoKm ?? t.ultimoKM ?? t.kmUltimo ?? t.km ?? t.numeroUltimoKm,
      usuarioSolicitud: usuarioSolicitudVisible,
      fechaSolicitud:
        t.dataFechaSolicitud ?? t.fechaSolicitud ?? t.tarjDtFechaSolicitud,
      loteTarjetaId: t.loteTarjetaId ?? t.loteTarjeta,
      nroMovil: t.nroMovil ?? t.numeroMovil ?? t.tarjNrMovil ?? t.nrMovil,
    };
  }, [init, tarjeta]);

  // ===== Regla de Bloqueo: Tarjeta Cancelada =====
  const isCancelada = useMemo(() => {
    const estadoText = String(view.estadoNombre || "").toUpperCase().trim();
    return (
      estadoText === "TARJETA CANCELADA" ||
      estadoText === "TARJETA SOLICITADA" ||
      estadoText === "TARJETA CANCELADA POR RENOVACION"
    );
  }, [view.estadoNombre]);

  const inputClass = `input ${
    isCancelada ? "bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed" : ""
  }`;

  // ===== Form editables =====
  const [form, setForm] = useState({
    estadoId: view.estadoId != null ? String(view.estadoId) : "",
    valorLimite: safeNum(view.valorLimite),
    dataCentroCosto: view.dataCentroCosto ?? "",
    textoObservaciones: view.textoObservaciones ?? "",
    nroMovil: view.nroMovil ?? "",
    capacidadTanque: safeNum(view.capacidadTanque),
    ultimoKm: safeNum(view.ultimoKm),
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm({
      estadoId: view.estadoId != null ? String(view.estadoId) : "",
      valorLimite: safeNum(view.valorLimite),
      dataCentroCosto: view.dataCentroCosto ?? "",
      textoObservaciones: view.textoObservaciones ?? "",
      nroMovil: view.nroMovil ?? "",
      capacidadTanque: safeNum(view.capacidadTanque),
      ultimoKm: safeNum(view.ultimoKm),
    });
  }, [
    view.estadoId,
    view.valorLimite,
    view.dataCentroCosto,
    view.textoObservaciones,
    view.nroMovil,
    view.capacidadTanque,
    view.ultimoKm,
  ]);

  // Validaciones
  const errs = useMemo(() => {
    const e = {};
    if (!isNonNegNumber(form.valorLimite)) {
      e.valorLimite = "Debe ser un número ≥ 0";
    }
    if (!isNonNegNumber(form.capacidadTanque)) {
      e.capacidadTanque = "Debe ser un número ≥ 0";
    }
    if (!isNonNegInteger(form.ultimoKm)) {
      e.ultimoKm = "Debe ser un entero ≥ 0";
    }
    return e;
  }, [form]);

  const isDirty = useMemo(
    () =>
      (form.estadoId ?? "") !== (view.estadoId != null ? String(view.estadoId) : "") ||
      numOrNull(form.valorLimite) !== numOrNull(view.valorLimite) ||
      (form.dataCentroCosto ?? "") !== (view.dataCentroCosto ?? "") ||
      (form.textoObservaciones ?? "") !== (view.textoObservaciones ?? "") ||
      (form.nroMovil ?? "") !== (view.nroMovil ?? "") ||
      numOrNull(form.capacidadTanque) !== numOrNull(view.capacidadTanque) ||
      numOrNull(form.ultimoKm) !== numOrNull(view.ultimoKm),
    [form, view],
  );

  const handleSave = async () => {
    setError("");

    if (Object.keys(errs).length > 0) return;

    if (!isDirty) {
      onClose();
      return;
    }

    if (String(form.estadoId) === ESTADO_CANCELADA_ID) {
      setConfirmCancel(true);
      return;
    }

    await doSave();
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="section-title">Editar estado de Tarjetas</h2>
        </div>

        <div className="modal-body">
          {initError && <div className="mb-4 text-red-600">{initError}</div>}
          {loadingInit ? (
            <div className="text-center text-gray-500 py-6">Cargando...</div>
          ) : (
            <>
              {error && <div className="mb-4 text-red-600">{error}</div>}

              {isCancelada && (
                <div className="mb-4 p-3 bg-red-100 text-red-800 border border-red-300 rounded">
                  No se permite editar una tarjeta <strong>Cancelada</strong>/<strong>Cancelada por renovacion</strong>/<strong>Solicitada</strong>.
                </div>
              )}

              <div className={`form-grid transition-opacity ${isCancelada ? "opacity-75 grayscale" : ""}`}>
                <FilaEdit
                  label="Estado"
                  input={
                    <select
                      value={form.estadoId ?? ""}
                      onChange={(e) => setField("estadoId", e.target.value)}
                      className={inputClass}
                      disabled={isCancelada || estadosOpts.length === 0}
                    >
                      <option value="">Seleccione estado...</option>
                      {estadosOpts.map((opt) => (
                        <option key={String(opt.value)} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  }
                />

                <Fila label="Cliente" value={view.clienteNombre} />
                <Fila label="EE.SS." value={view.eess} />
                <Fila label="Nro. de Tarjeta" value={view.numero} />
                <Fila
                  label="Vehículos con Tarjetas"
                  value={composeVehiculo(view)}
                />
                <Fila label="Tipo" value={view.tipo} />
                <Fila label="Fecha Alta" value={view.fechaAlta} />
                <Fila label="Fecha Baja" value={view.fechaBaja} />
                <Fila label="Saldo Cliente" value={formatMiles(view.saldoDisponibleCliente)} />
                <Fila label="Saldo Tarjeta" value={formatMiles(view.valorSaldo)} />

                <FilaEdit
                  label="Límite Tarjeta"
                  error={errs.valorLimite}
                  input={
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatMiles(form.valorLimite)}
                      onChange={(e) => {
                        const raw = parseMiles(e.target.value);
                        setField("valorLimite", raw);
                      }}
                      className={inputClass}
                      placeholder="0"
                      disabled={isCancelada}
                    />
                  }
                />

                <FilaEdit
                  label="Centro de Costo"
                  input={
                    <input
                      type="text"
                      value={form.dataCentroCosto ?? ""}
                      onChange={(e) =>
                        setField("dataCentroCosto", e.target.value)
                      }
                      className={inputClass}
                      placeholder="Código / Descripción"
                      disabled={isCancelada}
                    />
                  }
                />

                <FilaEdit
                  label="Observación"
                  input={
                    <textarea
                      rows={2}
                      value={form.textoObservaciones ?? ""}
                      onChange={(e) =>
                        setField("textoObservaciones", e.target.value)
                      }
                      className={inputClass}
                      placeholder="Escriba una observación"
                      disabled={isCancelada}
                    />
                  }
                />

                <FilaEdit
                  label="Cap. Tanque"
                  error={errs.capacidadTanque}
                  input={
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatMiles(form.capacidadTanque)}
                      onChange={(e) => {
                        const raw = parseMiles(e.target.value);
                        setField("capacidadTanque", raw);
                      }}
                      className={inputClass}
                      placeholder="0"
                      disabled={isCancelada}
                    />
                  }
                />

                <FilaEdit
                  label="Nr. Móvil"
                  input={
                    <input
                      type="text"
                      value={form.nroMovil ?? ""}
                      onChange={(e) => setField("nroMovil", e.target.value)}
                      className={inputClass}
                      placeholder="Nr. Móvil"
                      disabled={isCancelada}
                    />
                  }
                />

                <FilaEdit
                  label="Último KM"
                  error={errs.ultimoKm}
                  input={
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatMiles(form.ultimoKm)}
                      onChange={(e) => {
                        const raw = parseMiles(e.target.value);
                        setField("ultimoKm", raw);
                      }}
                      className={inputClass}
                      placeholder="0"
                      disabled={isCancelada}
                    />
                  }
                />

                <Fila
                  label="Fecha Vigencia Inicio"
                  value={view.fechaVigenciaInicio}
                />
                <Fila
                  label="Fecha Vigencia Final"
                  value={view.fechaVigenciaFin}
                />

                <Fila label="Usuario Solicitud" value={view.usuarioSolicitud} />
                <Fila label="Fecha Solicitud" value={view.fechaSolicitud} />
                <Fila label="Lote Tarjeta" value={view.loteTarjetaId} />
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <div className="btn-row">
            <button
              type="button"
              onClick={handleSave}
              className="btn-fil disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={
                saving ||
                Object.keys(errs).length > 0 ||
                !isDirty ||
                loadingInit ||
                isCancelada
              }
            >
              Guardar
            </button>
            <button type="button" onClick={onClose} className="btn-fil">
              Consultar
            </button>
          </div>
        </div>

        {confirmCancel && (
          <ConfirmModal
            message={
              <>
                ¿Está seguro que desea cancelar la tarjeta {view.numero}? Esta acción es{" "}
                <strong>irreversible</strong>.
              </>
            }
            onAccept={async () => {
              await doSave();
            }}
            onCancel={() => setConfirmCancel(false)}
          />
        )}
      </div>
    </div>
  );
}

/* ===== Subcomponentes ===== */
function Fila({ label, value }) {
  return (
    <div className="form-row gap-2">
      <div className="form-label">{label}:</div>
      <div className="flex-1">
        <div className="value">{value ?? ""}</div>
      </div>
    </div>
  );
}

function FilaEdit({ label, input, error }) {
  return (
    <div className="form-row gap-2 items-start">
      <div className="form-label">{label}:</div>
      <div className="flex-1">
        {input}
        {error && <div className="text-red-600 text-sm mt-1">{error}</div>}
      </div>
    </div>
  );
}

function ConfirmModal({ message, onAccept, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded shadow-lg p-4 max-w-sm w-full">
        <h2 className="font-semibold mb-2">Confirmar acción</h2>

        <p className="text-gray-800 mb-4">{message}</p>

        <div className="flex justify-end gap-2">
          <button
            onClick={onAccept}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Aceptar
          </button>

          <button
            onClick={onCancel}
            className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===== Helpers numéricos ===== */
function safeNum(v) {
  if (v === "" || v == null) return "";
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : "";
}

function numOrNull(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function isNonNegNumber(v) {
  if (v === "" || v == null) return true;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0;
}

function isNonNegInteger(v) {
  if (v === "" || v == null) return true;
  const n = Number(v);
  return Number.isInteger(n) && n >= 0;
}

function composeVehiculo(v) {
  const parts = [v.chapa, v.marca, v.modelo].filter(Boolean);
  return parts.length ? parts.join(" ") : "";
}